export interface PeppolMatch { name?: string; peppolId: string; regDate?: string }
export interface PeppolSearch {
  /** The UEN typed, if the query was one; null for a name search. */
  uen: string | null
  /** For a UEN: the exact match only. For a name: the first page of matches. */
  matches: PeppolMatch[]
  /** Total matches in the directory (may be more than `matches`). */
  total: number
}

/** Singapore UEN formats: 8 digits + letter (businesses), 9 digits + letter (local companies), and
 *  T/S/R + 2 digits + 2-letter entity type + 4 digits + letter (others). Accepts an optional "0195:SGUEN" prefix. */
export function normaliseUen(input: string): string | null {
  const v = input.trim().toUpperCase().replace(/^(0195:)?SGUEN/, '')
  return /^(\d{8,9}[A-Z]|[TSR]\d{2}[A-Z]{2}\d{4}[A-Z])$/.test(v) ? v : null
}

const PAGE = 8

/** Reads the directory's JSON into matches. For a UEN, keeps only the participant whose identifier ends in it. */
export function parseDirectory(json: any, uen: string | null): PeppolSearch {
  const all: PeppolMatch[] = (json?.matches ?? []).map((m: any) => {
    const ent = m.entities?.[0]
    return {
      name: ent?.name?.[0]?.name,
      peppolId: String(ent?.identifiers?.[0]?.value ?? m.participantID?.value ?? '').toUpperCase(),
      regDate: ent?.regDate,
    }
  })
  if (uen) {
    const hit = all.find(m => m.peppolId.endsWith(`SGUEN${uen}`))
    return { uen, matches: hit ? [hit] : [], total: hit ? 1 : 0 }
  }
  return { uen: null, matches: all.slice(0, PAGE), total: Number(json?.['total-result-count']) || all.length }
}

/** Searches the Peppol Directory (Singapore participants, via the /api/peppol proxy) by UEN or company name. */
export async function searchPeppol(query: string, signal?: AbortSignal): Promise<PeppolSearch> {
  const uen = normaliseUen(query)
  const q = uen ?? query.trim()
  const res = await fetch(`/api/peppol?${new URLSearchParams({ q, country: 'SG', rpc: String(PAGE) })}`, { signal })
  if (!res.ok) throw new Error(`Peppol Directory returned ${res.status}`)
  return parseDirectory(await res.json(), uen)
}
